import React from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import Calendar from '../../../assets/images/calendar.png'
import { makeStyles } from '../../../utils/hooks/makeStyles'

// Styles definition
const useStyles = makeStyles(() => ({
  dashboardContainer: {
    padding: '20px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
  },
  dashboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#183f7c',
    margin: 0,
  },
  dateFilters: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  dateFilterItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dateLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
  },
  datePickerWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  calendarIcon: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
  },
  datePickerUI: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    minWidth: '140px',
    '&:focus': {
      outline: 'none',
      borderColor: '#183f7c',
    },
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#666',
  },
  dashboardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  dashboardSection: {
    background: '#fff',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#183f7c',
    margin: '0 0 20px 0',
    paddingBottom: '12px',
    borderBottom: '2px solid #e8edf2',
  },
  chartContainer: {
    width: '100%',
    height: '400px',
    marginBottom: '20px',
  },
  chartGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
  },
  chartCard: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '20px',
    border: '1px solid #e8edf2',
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#183f7c',
    margin: '0 0 16px 0',
    paddingBottom: '10px',
    borderBottom: '2px solid #e8edf2',
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'linear-gradient(135deg, #183f7c 0%, #2563a8 100%)',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  summaryValue: {
    fontSize: '32px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  summaryLabel: {
    fontSize: '14px',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff',
    padding: '8px 16px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
  },
  toggleSwitch: {
    position: 'relative',
    width: '50px',
    height: '24px',
    background: '#ccc',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background 0.3s',
    '&.active': {
      background: '#183f7c',
    },
  },
  toggleSlider: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '20px',
    height: '20px',
    background: '#fff',
    borderRadius: '50%',
    transition: 'transform 0.3s',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    '&.active': {
      transform: 'translateX(26px)',
    },
  },
  primaryCard: {
    background: 'linear-gradient(135deg, #183f7c 0%, #2563a8 100%) !important',
  },
  maleCard: {
    background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%) !important',
  },
  femaleCard: {
    background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important',
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: '#999',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  '@media (max-width: 768px)': {
    dashboardContainer: {
      padding: '15px',
    },
    dashboardHeader: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    headerTitle: {
      fontSize: '24px',
    },
    dateFilters: {
      width: '100%',
      flexDirection: 'column',
      gap: '12px',
    },
    dateFilterItem: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    datePickerWrapper: {
      width: '100%',
    },
    datePickerUI: {
      width: '100%',
    },
    chartGrid: {
      gridTemplateColumns: '1fr',
    },
    chartContainer: {
      height: '300px',
    },
  },
}))

// Type definitions for API response mapping
interface AgeGroup {
  ageRange: string
  count: number
}

interface Department {
  name: string
  count: number
}

interface VisitType {
  type: string
  count: number
}

interface RegistrationData {
  total: number
  male: number
  female: number
  departments: Department[]
  ageGroups: AgeGroup[]
}

interface BasicHealthInfoData {
  total: number
  completed: number
  incomplete: number
}

interface VisitsData {
  visitTypes: VisitType[]
}

interface LabData {
  ORDERED: number
  COLLECTED: number
  SUBMITTED: number
  APPROVED: number
  COMPLETED: number
}

interface ECGData {
  total: number
  completed: number
  incomplete: number
  normal: number
  abnormal: number
}

interface PFTData {
  total: number
  completed: number
  incomplete: number
  normal: number
  abnormal: number
}

interface GradesData {
  a: number
  b: number
  c: number
  d: number
  e: number
}

interface DashboardData {
  registrations?: RegistrationData
  basicHealthInfo?: BasicHealthInfoData
  visits?: VisitsData
  lab?: LabData
  ecg?: ECGData
  pft?: PFTData
  grades?: GradesData
  result?: Record<string, string | number>
}

// Color schemes
const COLORS = {
  primary: '#183f7c',
  male: '#4a90e2',
  female: '#e91e63',
  success: '#4caf50',
  warning: '#ff9800',
  danger: '#f44336',
  info: '#2196f3',
  purple: '#667eea',
}

const PIE_COLORS = [
  '#183f7c',
  '#4a90e2',
  '#e91e63',
  '#4caf50',
  '#ff9800',
  '#2196f3',
  '#667eea',
  '#9c27b0',
]

// Fixed colors for known risk levels
const SLEEP_RISK_COLOR_MAP: Record<string, string> = {
  'low risk': '#4caf50', // green
  'intermediate risk': '#ff9800', // orange
  'high risk': '#f44336', // red
}

// Dynamic fallback colors (excluding the reserved risk colors above)
const SLEEP_DYNAMIC_COLORS = [
  '#183f7c',
  '#4a90e2',
  '#2196f3',
  '#667eea',
  '#9c27b0',
  '#e91e63',
]

// const getSleepColor = (key: string, dynamicIndex: number): string => {
//   const normalized = key.trim().toLowerCase()
//   return (
//     SLEEP_RISK_COLOR_MAP[normalized] ??
//     SLEEP_DYNAMIC_COLORS[dynamicIndex % SLEEP_DYNAMIC_COLORS.length]
//   )
// }

interface CampDashboardGraphicalProps {
  dashboardData: DashboardData
  loading: boolean
  fromDate: Date | null
  toDate: Date | null
  onFromDateChange: (date: Date | null) => void
  onToDateChange: (date: Date | null) => void
  onToggleView: () => void
  isGraphicalView: boolean
}

const CampDashboardGraphical: React.FC<CampDashboardGraphicalProps> = ({
  dashboardData,
  loading,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onToggleView,
  isGraphicalView,
}) => {
  const classes = useStyles()

  // Prepare chart data
  const genderData = [
    {
      name: 'Male',
      value: Number(dashboardData.registrations?.male) || 0,
      color: COLORS.male,
    },
    {
      name: 'Female',
      value: Number(dashboardData.registrations?.female) || 0,
      color: COLORS.female,
    },
  ]

  const basicHealthData = [
    {
      name: 'Completed',
      value: Number(dashboardData.basicHealthInfo?.completed) || 0,
      color: COLORS.success,
    },
    {
      name: 'Incomplete',
      value: Number(dashboardData.basicHealthInfo?.incomplete) || 0,
      color: COLORS.warning,
    },
  ]

  const labData = [
    {
      stage: 'Ordered',
      count: Number(dashboardData.lab?.ORDERED) || 0,
    },
    {
      stage: 'Collected',
      count: Number(dashboardData.lab?.COLLECTED) || 0,
    },
    { stage: 'Submitted', count: Number(dashboardData.lab?.SUBMITTED) || 0 },
    { stage: 'Approved', count: Number(dashboardData.lab?.APPROVED) || 0 },
    { stage: 'Completed', count: Number(dashboardData.lab?.COMPLETED) || 0 },
  ]

  const ecgData = [
    {
      category: 'Total',
      count: Number(dashboardData.ecg?.total) || 0,
      fill: COLORS.primary,
    },
    {
      category: 'Completed',
      count: Number(dashboardData.ecg?.completed) || 0,
      fill: COLORS.success,
    },
    {
      category: 'Incomplete',
      count: Number(dashboardData.ecg?.incomplete) || 0,
      fill: COLORS.warning,
    },
    {
      category: 'Normal',
      count: Number(dashboardData.ecg?.normal) || 0,
      fill: COLORS.info,
    },
    {
      category: 'Abnormal',
      count: Number(dashboardData.ecg?.abnormal) || 0,
      fill: COLORS.danger,
    },
  ]

  const pftData = [
    {
      category: 'Total',
      count: Number(dashboardData.pft?.total) || 0,
      fill: COLORS.primary,
    },
    {
      category: 'Completed',
      count: Number(dashboardData.pft?.completed) || 0,
      fill: COLORS.success,
    },
    {
      category: 'Incomplete',
      count: Number(dashboardData.pft?.incomplete) || 0,
      fill: COLORS.warning,
    },
    {
      category: 'Normal',
      count: Number(dashboardData.pft?.normal) || 0,
      fill: COLORS.info,
    },
    {
      category: 'Abnormal',
      count: Number(dashboardData.pft?.abnormal) || 0,
      fill: COLORS.danger,
    },
  ]

  const gradesData = [
    {
      grade: 'Grade A',
      count: Number(dashboardData.grades?.a) || 0,
      fill: COLORS.success,
    },
    {
      grade: 'Grade B',
      count: Number(dashboardData.grades?.b) || 0,
      fill: COLORS.info,
    },
    {
      grade: 'Grade C',
      count: Number(dashboardData.grades?.c) || 0,
      fill: COLORS.warning,
    },
    {
      grade: 'Grade D',
      count: Number(dashboardData.grades?.d) || 0,
      fill: COLORS.danger,
    },
    {
      grade: 'Grade E',
      count: Number(dashboardData.grades?.e) || 0,
      fill: COLORS.purple,
    },
  ]

  const gradesPieData = [
    {
      name: 'Grade A',
      value: Number(dashboardData.grades?.a) || 0,
      color: COLORS.success,
    },
    {
      name: 'Grade B',
      value: Number(dashboardData.grades?.b) || 0,
      color: COLORS.info,
    },
    {
      name: 'Grade C',
      value: Number(dashboardData.grades?.c) || 0,
      color: COLORS.warning,
    },
    {
      name: 'Grade D',
      value: Number(dashboardData.grades?.d) || 0,
      color: COLORS.danger,
    },
    {
      name: 'Grade E',
      value: Number(dashboardData.grades?.e) || 0,
      color: COLORS.purple,
    },
  ]

  // Sleep Analysis chart data — built dynamically from result keys
  let sleepDynamicIndex = 0
  const sleepColorCache: Record<string, string> = {}

  const getSleepColorCached = (key: string): string => {
    if (!sleepColorCache[key]) {
      const normalized = key.trim().toLowerCase()
      sleepColorCache[key] =
        SLEEP_RISK_COLOR_MAP[normalized] ??
        SLEEP_DYNAMIC_COLORS[sleepDynamicIndex++ % SLEEP_DYNAMIC_COLORS.length]
    }
    return sleepColorCache[key]
  }

  const sleepAnalysisChartData = Object.entries(
    dashboardData?.result ?? {}
  ).map(([key, value]) => ({
    category: key ?? '',
    count: Number(value ?? 0) || 0,
    fill: getSleepColorCached(key),
  }))

  const sleepAnalysisPieData = Object.entries(dashboardData?.result ?? {}).map(
    ([key, value]) => ({
      name: key ?? '',
      value: Number(value ?? 0) || 0,
      color: getSleepColorCached(key),
    })
  )

  // Transform visit types data to ensure count is a number
  const visitTypesData = (dashboardData.visits?.visitTypes || []).map(
    (visit) => ({
      type: visit.type,
      count: Number(visit.count) || 0,
    })
  )

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>
            {payload[0].name || payload[0].payload.name}
          </p>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            Count: <strong>{payload[0].value}</strong>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.dashboardHeader}>
        <h1 className={classes.headerTitle}>Dashboard - Graphical View</h1>
        <div className={classes.dateFilters}>
          <div className={classes.dateFilterItem}>
            <label className={classes.dateLabel}>From Date:</label>
            <div className={classes.datePickerWrapper}>
              <img
                alt='Calendar'
                src={Calendar}
                className={classes.calendarIcon}
              />
              <DatePicker
                className={classes.datePickerUI}
                placeholderText='DD-MM-YYYY'
                selected={fromDate}
                onChange={onFromDateChange}
                dateFormat='dd-MM-yyyy'
                maxDate={toDate || new Date()}
              />
            </div>
          </div>
          <div className={classes.dateFilterItem}>
            <label className={classes.dateLabel}>To Date:</label>
            <div className={classes.datePickerWrapper}>
              <img
                alt='Calendar'
                src={Calendar}
                className={classes.calendarIcon}
              />
              <DatePicker
                className={classes.datePickerUI}
                placeholderText='DD-MM-YYYY'
                selected={toDate}
                onChange={onToDateChange}
                dateFormat='dd-MM-yyyy'
                minDate={fromDate}
                maxDate={new Date()}
              />
            </div>
          </div>
          <div className={classes.toggleContainer}>
            <span className={classes.toggleLabel}>Table View</span>
            <div
              className={`${classes.toggleSwitch} ${
                isGraphicalView ? 'active' : ''
              }`}
              onClick={onToggleView}
            >
              <div
                className={`${classes.toggleSlider} ${
                  isGraphicalView ? 'active' : ''
                }`}
              />
            </div>
            <span className={classes.toggleLabel}>Graph View</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={classes.loadingState}>Loading dashboard data...</div>
      ) : (
        <div className={classes.dashboardContent}>
          {/* Section 1: Registrations */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Registrations</h2>

            <div className={classes.summaryCards}>
              <div className={`${classes.summaryCard} ${classes.primaryCard}`}>
                <div className={classes.summaryValue}>
                  {Number(dashboardData.registrations?.total) || 0}
                </div>
                <div className={classes.summaryLabel}>Total</div>
              </div>
              <div className={`${classes.summaryCard} ${classes.maleCard}`}>
                <div className={classes.summaryValue}>
                  {Number(dashboardData.registrations?.male) || 0}
                </div>
                <div className={classes.summaryLabel}>Male</div>
              </div>
              <div className={`${classes.summaryCard} ${classes.femaleCard}`}>
                <div className={classes.summaryValue}>
                  {Number(dashboardData.registrations?.female) || 0}
                </div>
                <div className={classes.summaryLabel}>Female</div>
              </div>
            </div>

            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Gender Distribution</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={genderData}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill='#8884d8'
                      dataKey='value'
                    >
                      {genderData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Departments</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart
                    data={dashboardData.registrations?.departments || []}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='name'
                      angle={-45}
                      textAnchor='end'
                      height={100}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.primary} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Age Groups</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={dashboardData.registrations?.ageGroups || []}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='ageRange' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.info} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 2: Basic Health Info */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Basic Health Info</h2>

            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Completion Status</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={basicHealthData}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill='#8884d8'
                      dataKey='value'
                    >
                      {basicHealthData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Status Overview</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart
                    data={[
                      {
                        status: 'Total',
                        count:
                          Number(dashboardData.basicHealthInfo?.total) || 0,
                        fill: COLORS.primary,
                      },
                      {
                        status: 'Completed',
                        count:
                          Number(dashboardData.basicHealthInfo?.completed) || 0,
                        fill: COLORS.success,
                      },
                      {
                        status: 'Incomplete',
                        count:
                          Number(dashboardData.basicHealthInfo?.incomplete) ||
                          0,
                        fill: COLORS.warning,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='status' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.primary}>
                      {[
                        {
                          status: 'Total',
                          count:
                            Number(dashboardData.basicHealthInfo?.total) || 0,
                          fill: COLORS.primary,
                        },
                        {
                          status: 'Completed',
                          count:
                            Number(dashboardData.basicHealthInfo?.completed) ||
                            0,
                          fill: COLORS.success,
                        },
                        {
                          status: 'Incomplete',
                          count:
                            Number(dashboardData.basicHealthInfo?.incomplete) ||
                            0,
                          fill: COLORS.warning,
                        },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 3: Visits */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Visits</h2>

            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Visit Types Distribution</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={visitTypesData}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={({ type, percent }) =>
                        `${type}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill='#8884d8'
                      dataKey='count'
                      nameKey='type'
                    >
                      {visitTypesData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Visit Types Count</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={visitTypesData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='type'
                      angle={-45}
                      textAnchor='end'
                      height={100}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.purple}>
                      {visitTypesData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 4: Lab */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Lab</h2>

            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Lab Process Flow</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <LineChart data={labData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='stage'
                      angle={-45}
                      textAnchor='end'
                      height={100}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='count'
                      stroke={COLORS.primary}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Lab Stages</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={labData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='stage'
                      angle={-45}
                      textAnchor='end'
                      height={100}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.info} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 5: ECG/PFT */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>ECG / PFT</h2>

            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>ECG Statistics</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={ecgData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='category' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.primary}>
                      {ecgData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>PFT Statistics</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={pftData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='category' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.primary}>
                      {pftData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>ECG vs PFT Comparison</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart
                    data={[
                      {
                        test: 'ECG Total',
                        count: Number(dashboardData.ecg?.total) || 0,
                      },
                      {
                        test: 'PFT Total',
                        count: Number(dashboardData.pft?.total) || 0,
                      },
                      {
                        test: 'ECG Completed',
                        count: Number(dashboardData.ecg?.completed) || 0,
                      },
                      {
                        test: 'PFT Completed',
                        count: Number(dashboardData.pft?.completed) || 0,
                      },
                      {
                        test: 'ECG Normal',
                        count: Number(dashboardData.ecg?.normal) || 0,
                      },
                      {
                        test: 'PFT Normal',
                        count: Number(dashboardData.pft?.normal) || 0,
                      },
                      {
                        test: 'ECG Abnormal',
                        count: Number(dashboardData.ecg?.abnormal) || 0,
                      },
                      {
                        test: 'PFT Abnormal',
                        count: Number(dashboardData.pft?.abnormal) || 0,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='test'
                      angle={-45}
                      textAnchor='end'
                      height={100}
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.purple} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 6: Grades */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Grades Distribution</h2>

            {/* Charts Grid */}
            <div className={classes.chartGrid}>
              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Grades Bar Chart</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={gradesData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='grade' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='count' fill={COLORS.primary}>
                      {gradesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>
                  Grades Distribution (Pie Chart)
                </h3>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={gradesPieData}
                      cx='50%'
                      cy='50%'
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill='#8884d8'
                      dataKey='value'
                    >
                      {gradesPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className={classes.chartCard}>
                <h3 className={classes.chartTitle}>Grades Trend</h3>
                <ResponsiveContainer width='100%' height={300}>
                  <LineChart data={gradesData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='grade' />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='count'
                      stroke={COLORS.primary}
                      strokeWidth={3}
                      dot={{ r: 6, fill: COLORS.primary }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* Section 7: Sleep Analysis */}
          {sleepAnalysisChartData.length > 0 && (
            <section className={classes.dashboardSection}>
              <h2 className={classes.sectionTitle}>Sleep Analysis</h2>
              <div className={classes.chartGrid}>
                <div className={classes.chartCard}>
                  <h3 className={classes.chartTitle}>
                    Risk Distribution (Bar)
                  </h3>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart data={sleepAnalysisChartData}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis dataKey='category' />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey='count' fill={COLORS.primary}>
                        {sleepAnalysisChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className={classes.chartCard}>
                  <h3 className={classes.chartTitle}>
                    Risk Distribution (Pie)
                  </h3>
                  <ResponsiveContainer width='100%' height={300}>
                    <PieChart>
                      <Pie
                        data={sleepAnalysisPieData}
                        cx='50%'
                        cy='50%'
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                        fill='#8884d8'
                        dataKey='value'
                      >
                        {sleepAnalysisPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}

export default CampDashboardGraphical
